import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import client from "@/lib/api";

const FALLBACK = [{
  id: "default",
  image: "https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  title: "منصة الجملة الأولى في ليبيا",
  subtitle: "من تاجر لتاجر · تربط بين التجار وتفتح لك فرصاً أكبر بأسعار تنافسية.",
  cta_text: "ابدأ الآن",
  cta_link: "/browse",
  duration: 5,
}];

export default function HeroCarousel() {
  const nav = useNavigate();
  const [slides, setSlides] = useState(FALLBACK);
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    client.get("/banners").then((r) => {
      if (Array.isArray(r.data) && r.data.length) setSlides(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (idx >= slides.length) setIdx(0); }, [slides.length, idx]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const cur = slides[idx] || slides[0];
    const ms = Math.max(1, Number(cur?.duration) || 5) * 1000;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!paused.current) setIdx((i) => (i + 1) % slides.length);
    }, ms);
    return () => clearTimeout(timer.current);
  }, [idx, slides]);

  const go = (i) => setIdx((i + slides.length) % slides.length);

  const onCta = (link) => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) window.open(link, "_blank");
    else nav(link);
  };

  const s = slides[idx] || slides[0];

  return (
    <div
      data-testid="hero-carousel"
      className="mx-4 mt-4 rounded-3xl overflow-hidden relative group"
      style={{ minHeight: 190 }}
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      {slides.map((sl, i) => (
        <div key={sl.id || i} className="absolute inset-0 transition-opacity duration-700 ease-in-out" style={{ opacity: i === idx ? 1 : 0, zIndex: i === idx ? 1 : 0 }}>
          <img src={sl.image} alt={sl.title || "إعلان"} className="absolute inset-0 w-full h-full object-cover" style={{ animation: i === idx ? "kBannerZoom 6s ease-out both" : "none" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(270deg, rgba(18,32,58,.35), rgba(18,32,58,.92))" }} />
        </div>
      ))}

      <div className="relative z-10 p-6 md:p-8 max-w-[560px]" style={{ minHeight: 190 }}>
        {s?.title && <h1 data-testid="hero-title" className="font-head text-[24px] md:text-[30px] font-black text-white leading-tight">{s.title}</h1>}
        {s?.subtitle && <p className="text-[13px] md:text-[14px] mt-2 mb-4" style={{ color: "#E4D9C4" }}>{s.subtitle}</p>}
        {s?.cta_text && (
          <button data-testid="hero-cta" onClick={() => onCta(s.cta_link)} className="k-tap px-6 py-3 rounded-2xl font-bold text-[14px]" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}>{s.cta_text}</button>
        )}
      </div>

      {slides.length > 1 && (
        <>
          <button data-testid="hero-prev" onClick={() => go(idx + 1)} className="k-tap absolute top-1/2 -translate-y-1/2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(255,255,255,.22)", color: "#fff", backdropFilter: "blur(6px)" }}><ChevronRight size={18} /></button>
          <button data-testid="hero-next" onClick={() => go(idx - 1)} className="k-tap absolute top-1/2 -translate-y-1/2 left-2 z-20 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(255,255,255,.22)", color: "#fff", backdropFilter: "blur(6px)" }}><ChevronLeft size={18} /></button>
          <div className="absolute bottom-3 left-0 right-0 z-20 flex items-center justify-center gap-1.5">
            {slides.map((_, i) => (
              <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setIdx(i)} className="k-tap rounded-full transition-all" style={{ width: i === idx ? 20 : 7, height: 7, background: i === idx ? "var(--k-gold)" : "rgba(255,255,255,.5)" }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
